import type { UserProjectAccess } from './state/userManagementSlice';

/**
 * Plan and diff user↔site role assignments for GraphQL `users.removeProjects` / `users.upsertProjects`.
 * Consumed by the permissions matrix (immediate persist) and edit-user footer save.
 *
 * Role-level add/remove/replace operations per project within an organisation.
 * Used by the matrix for bulk UX and optional diagnostics.
 */
export type UserProjectRoleChangeSet = {
    add?: { projectId: string; role: string }[];
    remove?: { projectId: string; role: string }[];
    replace?: { projectId: string; roles: string[]; remark?: string | null }[];
};

/** One row for `users.upsertProjects` (GraphQL `UserProjectAccessInput`). */
export type UpsertUserProjectAccessInput = {
    projectId: string;
    roles: string[];
    remark?: string | null;
};

/** Arguments for saving project access: current form rows plus optional role-level change summary. */
export type PersistUserProjectAccessPayload = {
    syncedProjects: UserProjectAccess[];
    /** Optional; used by the matrix for bulk apply when present. */
    roleChanges?: UserProjectRoleChangeSet;
};

const sortedRoles = (roles: (string | null | undefined)[] | null | undefined) =>
    [...(roles ?? [])].filter((r): r is string => Boolean(r)).sort();

/**
 * From before/after project-access lists for one organisation, derive `users.removeProjects` ids
 * and `users.upsertProjects` rows (scoped to `organisationProjectIds`).
 */
export function planUserProjectAccessMutations(
    beforeList: UserProjectAccess[],
    afterList: UserProjectAccess[],
    organisationProjectIds: string[]
): { removeProjectIds: string[]; upserts: UpsertUserProjectAccessInput[] } {
    const removeProjectIds: string[] = [];
    const upserts: UpsertUserProjectAccessInput[] = [];

    for (const projectId of organisationProjectIds) {
        const beforeRow = beforeList.find(p => p.projectId === projectId);
        const afterRow = afterList.find(p => p.projectId === projectId);
        const b = sortedRoles(beforeRow?.roles ?? null);
        const a = sortedRoles(afterRow?.roles ?? null);
        const same = b.length === a.length && b.every((r, i) => r === a[i]);
        if (same) {
            continue;
        }
        if (a.length === 0 && b.length > 0) {
            removeProjectIds.push(projectId);
            continue;
        }
        if (a.length > 0 && afterRow) {
            upserts.push({
                projectId,
                roles: [...(afterRow.roles ?? [])].filter((r): r is string => Boolean(r)),
                remark: afterRow.remark ?? undefined,
            });
        }
    }

    return { removeProjectIds, upserts };
}

/** Per-project role diffs between two access lists, limited to `organisationProjectIds`. */
export function diffUserProjectRoles(
    beforeList: UserProjectAccess[],
    afterList: UserProjectAccess[],
    organisationProjectIds: string[]
): UserProjectRoleChangeSet {
    const inScope = (p: UserProjectAccess) => p.projectId && organisationProjectIds.includes(p.projectId);

    const roleSetByProject = (list: UserProjectAccess[]) => {
        const m = new Map<string, Set<string>>();
        list.filter(inScope).forEach(p => {
            m.set(p.projectId, new Set((p.roles || []).filter(Boolean)));
        });
        return m;
    };

    const beforeMap = roleSetByProject(beforeList);
    const afterMap = roleSetByProject(afterList);
    const allIds = new Set<string>([...beforeMap.keys(), ...afterMap.keys()]);

    const add: { projectId: string; role: string }[] = [];
    const remove: { projectId: string; role: string }[] = [];

    for (const projectId of allIds) {
        const b = beforeMap.get(projectId) ?? new Set<string>();
        const a = afterMap.get(projectId) ?? new Set<string>();
        for (const role of a) {
            if (!b.has(role)) {
                add.push({ projectId, role });
            }
        }
        for (const role of b) {
            if (!a.has(role)) {
                remove.push({ projectId, role });
            }
        }
    }

    return { add, remove };
}

/** Stable shape for equality checks: filter to organisation projects, sort roles and rows, normalise remarks. */
export function normalizeUserProjectsForEquality(projects: UserProjectAccess[], organisationProjectIds: string[]) {
    return [...projects]
        .filter(p => p.projectId && organisationProjectIds.includes(p.projectId))
        .map(p => ({
            projectId: p.projectId,
            roles: [...(p.roles || [])].filter(Boolean).sort(),
            remark: p.remark ?? '',
        }))
        .sort((a, b) => a.projectId.localeCompare(b.projectId));
}
